import { useMemo, useState } from 'react';
import { InsightsPanel } from '../ndt/components/InsightsPanel';
import { MLInsightsPanel } from '../ndt/components/MLInsightsPanel';
import {
  InsightsSectionNav,
  type InsightsSectionTab,
} from '../weld-analytics/components/shared/InsightsSectionNav';

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

type ClassicInsights = Parameters<typeof InsightsPanel>[0]['insights'];
type MlInsights = Parameters<typeof MLInsightsPanel>[0]['insights'];

const AI_PREFIX = 'ai:';

function classicTabsFromInsights(insights: ClassicInsights): InsightsSectionTab[] {
  if (!insights) {
    return [
      { id: 'summary', label: 'Executive Summary', count: null },
      { id: 'patterns', label: 'Patterns', count: 0 },
      { id: 'correlations', label: 'Correlations', count: 0 },
      { id: 'anomalies', label: 'Anomalies', count: 0 },
      { id: 'temporal', label: 'Temporal Trends', count: 0 },
      { id: 'welder', label: 'Welder Performance', count: null },
      { id: 'rootcause', label: 'Root Cause Analysis', count: 0 },
      { id: 'comparative', label: 'Comparative Analysis', count: 0 },
      { id: 'recommendations', label: 'Recommendations', count: 0 },
    ];
  }

  const welderCount =
    (insights.welderPerformance?.highRiskWelders?.length || 0) +
    (insights.welderPerformance?.consistentPerformers?.length || 0) +
    (insights.welderPerformance?.welderDefectPatterns?.length || 0) +
    (insights.welderPerformance?.welderCombinations?.length || 0);

  return [
    { id: 'summary', label: 'Executive Summary', count: null },
    { id: 'patterns', label: 'Patterns', count: insights.patterns?.length || 0 },
    { id: 'correlations', label: 'Correlations', count: insights.correlations?.length || 0 },
    { id: 'anomalies', label: 'Anomalies', count: insights.anomalies?.length || 0 },
    { id: 'temporal', label: 'Temporal Trends', count: insights.temporalTrends?.length || 0 },
    { id: 'welder', label: 'Welder Performance', count: welderCount > 0 ? welderCount : null },
    {
      id: 'rootcause',
      label: 'Root Cause Analysis',
      count: insights.rootCauseAnalysis?.insights?.length || 0,
    },
    {
      id: 'comparative',
      label: 'Comparative Analysis',
      count: insights.comparativeAnalysis?.length || 0,
    },
    {
      id: 'recommendations',
      label: 'Recommendations',
      count: insights.recommendations?.length || 0,
    },
  ];
}

function aiTabsFromInsights(insights: MlInsights): InsightsSectionTab[] {
  return [
    {
      id: `${AI_PREFIX}patterns`,
      label: 'Patterns',
      count: insights?.patterns?.length ?? 0,
      badge: 'AI',
    },
    {
      id: `${AI_PREFIX}correlations`,
      label: 'Relationships',
      count: insights?.correlations?.length ?? 0,
      badge: 'AI',
    },
    {
      id: `${AI_PREFIX}anomalies`,
      label: 'Unusual Cases',
      count: insights?.anomalies?.length ?? 0,
      badge: 'AI',
    },
  ];
}

function NdtStatus({
  status,
  error,
  emptyText,
  onRetry,
}: {
  status: string;
  error: string | null;
  emptyText: string;
  onRetry: () => void;
}) {
  if (status === 'error') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
        <p className="text-sm text-readable-red">{error || 'NDT data failed to load.'}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 text-sm font-medium text-blueprint-blue underline"
        >
          Retry
        </button>
      </div>
    );
  }
  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-hero-red" />
      </div>
    );
  }
  return (
    <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">{emptyText}</p>
  );
}

export interface CombinedNdtInsightsProps {
  classicInsights: ClassicInsights;
  classicStatus: Status;
  classicError: string | null;
  mlInsights: MlInsights;
  mlStatus: Status;
  mlError: string | null;
  onRetry: () => void;
}

/**
 * One NDT insights pill strip: classic tabs + AI Summary/Patterns/Relationships/Unusual Cases.
 */
export function CombinedNdtInsights({
  classicInsights,
  classicStatus,
  classicError,
  mlInsights,
  mlStatus,
  mlError,
  onRetry,
}: CombinedNdtInsightsProps) {
  const classicReady = classicStatus === 'ready' && classicInsights;
  const mlReady = mlStatus === 'ready' && mlInsights;

  const tabs = useMemo(() => {
    const classic = classicTabsFromInsights(classicReady ? classicInsights : null);
    const ai = aiTabsFromInsights(mlReady ? mlInsights : null);
    return [...classic, ...ai];
  }, [classicReady, classicInsights, mlReady, mlInsights]);

  const [activeId, setActiveId] = useState('summary');
  const isAi = activeId.startsWith(AI_PREFIX);
  const sectionId = isAi ? activeId.slice(AI_PREFIX.length) : activeId;

  return (
    <div className="space-y-6" data-testid="combined-ndt-insights-unified">
      <InsightsSectionNav
        aria-label="NDT insights sections"
        tabs={tabs}
        activeId={activeId}
        onChange={setActiveId}
      />

      {!isAi ? (
        classicReady ? (
          <InsightsPanel
            insights={classicInsights}
            isLoading={false}
            hideNav
            activeSection={sectionId}
            onActiveSectionChange={(id) => setActiveId(id)}
          />
        ) : (
          <NdtStatus
            status={classicStatus}
            error={classicError}
            emptyText="No NDT insights for this scope."
            onRetry={onRetry}
          />
        )
      ) : mlReady ? (
        <MLInsightsPanel
          insights={mlInsights}
          isLoading={false}
          hideNav
          showAiBadges={false}
          activeSection={sectionId}
          onActiveSectionChange={(id) => setActiveId(`${AI_PREFIX}${id}`)}
        />
      ) : (
        <NdtStatus
          status={mlStatus}
          error={mlError}
          emptyText="No NDT AI insights for this scope."
          onRetry={onRetry}
        />
      )}
    </div>
  );
}
